import SendIcon from "@mui/icons-material/Send";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import LoadingButton from "_shared/_views/loading/loading-button";
import { refreshStaleStateOnce } from "_shared/_errors/stale-state";
import { readApiError } from "_shared/_errors/api-error";
import PlaceAutocomplete from "_shared/addresses/_views/place-autocomplete";
import Address from "_shared/addresses/address";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import TitleInput from "_shared/titles/_views/inputs/title-input";
import Title from "_shared/titles/title";
import Guid from "_shared/guids/guid";
import EntityNameInput from "legal-entities/entity-names/_views/inputs/entity-name-input";
import EntityName from "legal-entities/entity-names/entity-name";
import InvitationMessageInput from "legal-entities/invitations/messages/_views/inputs/invitation-message-input";
import InvitationMessage from "legal-entities/invitations/messages/invitation-message";
import {
  EntityOfficerInvitationServiceError,
  OfficerSetupOperationStatus,
  OfficerSetupPendingError,
  useInviteEntityOfficerMutation,
  useLazyGetOfficerSetupStatusQuery,
} from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import { EntityOfficerInvitationParams } from "legal-entities/invitations/officer-invitations/_schema/entity-officer-invitation-schema";
import EntityOfficerInvitation, {
  InvalidEntityOfficerInvitationError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { RegistrationInfoParams } from "legal-entities/registration-info/_schema/registration-info-schema";
import RegistrationInfo, { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo, useRef, useState } from "react";

const OfficerInvitationFormContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  position: "relative",
  width: "100%",
}));
const Fieldset = styled("fieldset")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  minWidth: 0,
  flex: "1 1 300px",
  "&:last-of-type": {
    marginBottom: theme.spacing(2),
  },
}));
const OfficerInvitationFields = styled(Box)(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));
const SendInvitationButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const PENDING_OFFICER_SETUP_STORAGE_KEY = "attorneyhub:pending-officer-setup";

type InvitationFormErrors = {
  general?: string;
  title?: string;
  name?: string;
  email?: string;
  entityName?: string;
  location?: string;
  message?: string;
};

type OfficerOnboardingTemplate = {
  id: string;
  displayName: string;
  isDefault?: boolean;
  version: string;
  workflowVersion: number;
  participationMode: string;
  negotiable: boolean;
};

type EntityOfficerInvitationFormProps = {
  className?: string;
  onInvitationCreated?: (invitation: EntityOfficerInvitation) => void;
  templates?: ReadonlyArray<OfficerOnboardingTemplate>;
  templateCatalogRequired?: boolean;
  templatesLoading?: boolean;
  templatesError?: boolean;
  onRetryTemplates?: () => void | Promise<void>;
};

function ignoreCreatedInvitation() {}

export default function EntityOfficerInvitationForm(props: Readonly<EntityOfficerInvitationFormProps>) {
  const {
    className,
    templates = [],
    templateCatalogRequired = false,
    templatesLoading = false,
    templatesError = false,
    onRetryTemplates,
  } = props;
  // Sending invalidates the open-invitation list, so a caller that renders the query result needs
  // no notification. The callback stays for one that tracks what it sent itself.
  const onInvitationCreated = props.onInvitationCreated ?? ignoreCreatedInvitation;

  const locationServicesRef = useRef<boolean>(false);

  const [inviteeEmail, setInviteeEmail] = useState<EmailAddress>();
  const [inviteeEntityName, setInviteeEntityName] = useState<EntityName>();
  const [inviteeTitle, setInviteeTitle] = useState<Title>();
  const [inviteeAddress, setInviteeAddress] = useState<Address>();
  const [inviteeName, setInviteeName] = useState<Name>();
  const [invitationMessage, setInvitationMessage] = useState<InvitationMessage>();
  const defaultTemplate = templates.find((template) => template.isDefault) ?? templates[0];
  const [selectedTemplate, setSelectedTemplate] = useState<OfficerOnboardingTemplate | undefined>(defaultTemplate);
  const [templateSelectionNeedsChange, setTemplateSelectionNeedsChange] = useState(false);
  const initializedTemplateSelectionRef = useRef(Boolean(defaultTemplate));
  const availableSelectedTemplate = templates.find((template) => template.id === selectedTemplate?.id);
  const selectedTemplateUnavailable = Boolean(
    selectedTemplate && (!availableSelectedTemplate || templateSelectionNeedsChange),
  );
  const selectedTemplateId = selectedTemplate?.id;
  const [messageDebouncing, setMessageDebouncing] = useState<boolean>(false);

  useEffect(() => {
    if (initializedTemplateSelectionRef.current || !defaultTemplate) return;
    initializedTemplateSelectionRef.current = true;
    setSelectedTemplate(defaultTemplate);
  }, [defaultTemplate]);

  const [key, setKey] = useState<number>(0);

  const [sending, setSending] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<InvitationFormErrors>({});

  const [inviteEntityOfficer] = useInviteEntityOfficerMutation();
  const [getOfficerSetupStatus] = useLazyGetOfficerSetupStatusQuery();
  const [pendingOperation, setPendingOperation] = useState<{ id: Guid; traceId?: string } | undefined>(
    readPendingOfficerSetup,
  );

  function updateFormError(type: keyof InvitationFormErrors, error?: string) {
    if (!error) return;
    let errors: InvitationFormErrors = { ...formErrors };
    errors[type] = error;
    setFormErrors(errors);
  }

  const invitationParams = useMemo((): Partial<
    Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo">
  > => {
    return {
      email: inviteeEmail,
      title: inviteeTitle,
      name: inviteeName,
      entityName: inviteeEntityName,
      entityLocation: inviteeAddress,
      message: invitationMessage,
      onboardingTemplateId: selectedTemplateId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inviteeEmail,
    inviteeTitle,
    inviteeName?.firstName,
    inviteeName?.lastName,
    inviteeEntityName,
    inviteeAddress?.address1,
    inviteeAddress?.address2,
    inviteeAddress?.city,
    inviteeAddress?.country,
    inviteeAddress?.coordinates,
    inviteeAddress?.postalCode,
    inviteeAddress?.state,
    invitationMessage,
    selectedTemplateId,
  ]);

  const formIsValid = useMemo((): boolean => {
    try {
      validateFormFields(invitationParams);
      return true;
    } catch {
      return false;
    }
  }, [invitationParams]);

  function buildInvitation(
    params: Partial<Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo">>,
  ): EntityOfficerInvitation | undefined {
    try {
      validateFormFields(params);
      return EntityOfficerInvitation.create({
        params: {
          ...params,
          registrationInfo: RegistrationInfo.create({
            params,
            options: { silenceSchemaErrors: true },
          }),
        },
        options: { silenceSchemaErrors: true },
      });
    } catch (error) {
      if (error instanceof InvalidEntityOfficerInvitationError || error instanceof InvalidRegistrationInfoError) {
        setFormErrors({ general: error.message });
        return;
      }
      throw error;
    }
  }

  async function sendInvitation() {
    setSending(true);
    let operationReference = pendingOperation?.traceId ?? pendingOperation?.id.value;

    try {
      if (pendingOperation) {
        const status = await getOfficerSetupStatus(pendingOperation.id, false).unwrap();
        if (status.status === OfficerSetupOperationStatus.Cancelled) {
          setPendingOperation(undefined);
          clearPendingOfficerSetup();
          enqueueSnackbar("This invitation is closed and was not sent. Start a new invitation if it is still required.", {
            variant: "warning",
          });
          return;
        }
        if (status.status === OfficerSetupOperationStatus.NeedsStaffResolution) {
          const reference = pendingOperation.traceId ?? pendingOperation.id.value;
          setPendingOperation(undefined);
          clearPendingOfficerSetup();
          enqueueSnackbar(`This invitation is too old to send automatically. Ask support staff to review it. Reference: ${reference}.`, {
            variant: "warning",
          });
          return;
        }
        if (status.status !== "Completed" || !status.invitationId) {
          const reference = pendingOperation.traceId ?? pendingOperation.id.value;
          enqueueSnackbar(`Invitation delivery is still being checked. Reference: ${reference}.`, {
            variant: "warning",
          });
          return;
        }
        enqueueSnackbar("Entity officer invitation delivery confirmed", { variant: "success" });
        clearForm();
        return;
      }

      const builtInvitation = buildInvitation(invitationParams);
      if (!builtInvitation) throw new InvalidEntityOfficerInvitationError("Invitation is invalid");
      const operationId = Guid.generate();
      const startedOperation = { id: operationId };
      operationReference = operationId.value;
      setPendingOperation(startedOperation);
      writePendingOfficerSetup(startedOperation);
      const updatedInvitation = await inviteEntityOfficer({ invitation: builtInvitation, operationId }).unwrap();
      enqueueSnackbar("Sent entity officer invitation", { variant: "success" });
      onInvitationCreated(updatedInvitation);
      clearForm();
    } catch (ex) {
      console.error(ex);
      if (ex instanceof OfficerSetupPendingError) {
        const pending = { id: ex.operationId, traceId: ex.traceId };
        setPendingOperation(pending);
        writePendingOfficerSetup(pending);
        const reference = ex.traceId ?? ex.operationId.value;
        enqueueSnackbar(`Invitation delivery is being checked. Reference: ${reference}.`, { variant: "warning" });
        return;
      }
      const rawError = ex instanceof EntityOfficerInvitationServiceError ? ex.error : ex;
      if (ex instanceof EntityOfficerInvitationServiceError) {
        setPendingOperation(undefined);
        clearPendingOfficerSetup();
      }
      const apiError = readApiError(rawError);
      if (selectedTemplateId && onRetryTemplates &&
          apiError.code === "onboarding_template_unavailable") {
        const refreshed = await refreshStaleStateOnce(
          rawError,
          `officer-invitation:onboarding-template:${selectedTemplateId}`,
          async () => {
            setTemplateSelectionNeedsChange(true);
            await onRetryTemplates();
          },
        );
        if (refreshed) {
          enqueueSnackbar("Onboarding templates changed. Select an available option and send the invitation again.", {
            variant: "warning",
          });
          return;
        }
      }
      if (apiError.code === "officer_invitation_closed") {
        enqueueSnackbar("This invitation is closed and was not sent. Start a new invitation if it is still required.", {
          variant: "warning",
        });
        return;
      }
      if (apiError.code === "officer_invitation_staff_resolution_required") {
        const reference = apiError.traceId ?? operationReference;
        const referenceText = reference ? ` Reference: ${reference}.` : "";
        enqueueSnackbar(`This invitation is too old to send automatically. Ask support staff to review it.${referenceText}`, {
          variant: "warning",
        });
        return;
      }
      enqueueSnackbar("Failed to send entity officer invitation", {
        variant: "error",
      });
    } finally {
      setSending(false);
    }
  }

  function clearForm() {
    setInviteeEmail(undefined);
    setInviteeAddress(undefined);
    setInviteeTitle(undefined);
    setInviteeEntityName(undefined);
    setInviteeName(undefined);
    setInvitationMessage(undefined);
    setSelectedTemplate(defaultTemplate);
    setTemplateSelectionNeedsChange(false);
    setPendingOperation(undefined);
    clearPendingOfficerSetup();
    setKey((i) => i + 1);
  }

  function startNewInvitation() {
    setPendingOperation(undefined);
    clearPendingOfficerSetup();
  }

  return (
    <OfficerInvitationFormContainer className={className}>
      <InvitationFormTitle variant="h6">Invite Entity Officer</InvitationFormTitle>
      <OfficerInvitationFields>
        <Fieldset>
          <legend>
            <Typography variant="subtitle2">Officer Details</Typography>
          </legend>
          <TitleInput
            key={`title-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            initialValue={inviteeTitle ?? undefined}
            onChange={(updatedTitle?: Title | undefined, error?: string) => {
              setInviteeTitle(updatedTitle);
              updateFormError("title", error);

              if (updatedTitle && !error) {
                buildInvitation({
                  ...invitationParams,
                  title: updatedTitle,
                });
              }
            }}
          />
          <NameInput
            key={`name-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            initialValue={inviteeName}
            onChange={(updatedName?: Name | undefined, errorMessage?: string) => {
              setInviteeName(updatedName);
              updateFormError("name", errorMessage);

              if (updatedName && !errorMessage) {
                buildInvitation({
                  ...invitationParams,
                  name: updatedName,
                });
              }
            }}
            direction="column"
          />
          <EmailAddressInput
            key={`email-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            initialValue={inviteeEmail}
            onChange={(updatedEmail?: EmailAddress, error?: string) => {
              if (updatedEmail) {
                setInviteeEmail(updatedEmail);
                buildInvitation({
                  ...invitationParams,
                  email: updatedEmail,
                });
              }
              updateFormError("email", error);
            }}
          />
        </Fieldset>
        <Fieldset>
          <legend>
            <Typography variant="subtitle2">Entity Details</Typography>
          </legend>
          <EntityNameInput
            key={`entityName-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            label="Name"
            initialValue={inviteeEntityName}
            onChange={(updatedEntityName?: EntityName | undefined, error?: string) => {
              setInviteeEntityName(updatedEntityName);
              updateFormError("entityName", error);

              if (updatedEntityName && !error) {
                buildInvitation({
                  ...invitationParams,
                  entityName: updatedEntityName,
                });
              }
            }}
          />
          <PlaceAutocomplete
            key={`location-${key}`}
            label="Location"
            variant="outlined"
            required
            value={inviteeAddress ?? null}
            locationServicesDisabledRef={locationServicesRef}
            onLocationServicesDisabledChange={(newValue) => {
              locationServicesRef.current = newValue;
            }}
            onChange={(address: Address | null, error?: string) => {
              setInviteeAddress(address ?? undefined);
              updateFormError("location", error);

              if (address && !error) {
                buildInvitation({
                  ...invitationParams,
                  entityLocation: address,
                });
              }
            }}
          />
        </Fieldset>
        <InvitationMessageInput
          key={`message-${key}`}
          idPrefix="officer-invitation"
          initialValue={invitationMessage}
          onChange={(updatedMessage?: InvitationMessage, error?: string) => {
            setInvitationMessage(updatedMessage);
            updateFormError("message", error);

            if (updatedMessage && !error) {
              buildInvitation({
                ...invitationParams,
                message: updatedMessage,
              });
            }
          }}
          onDebounceStart={() => {
            setMessageDebouncing(true);
          }}
          onDebounceEnd={() => {
            setMessageDebouncing(false);
          }}
        />
        {templatesLoading && templateCatalogRequired && (
          <Alert severity="info" aria-live="polite" sx={{ mt: 1, width: "100%" }}>
            Loading onboarding templates...
          </Alert>
        )}
        {templatesError && templateCatalogRequired && (
          <Alert
            severity="error"
            sx={{ mt: 1, width: "100%" }}
            action={onRetryTemplates ? (
              <Button color="inherit" size="small" onClick={onRetryTemplates}>
                Try Again
              </Button>
            ) : undefined}
          >
            Onboarding templates are unavailable. You cannot send an invitation until they load.
          </Alert>
        )}
        {!templatesLoading && !templatesError && templates.length > 0 && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="officer-invitation-template-label">Onboarding template</InputLabel>
            <Select
              labelId="officer-invitation-template-label"
              id="officer-invitation-template"
              label="Onboarding template"
              value={selectedTemplateId ?? ""}
              error={selectedTemplateUnavailable}
              onChange={(event) => {
                setSelectedTemplate(templates.find((template) => template.id === event.target.value));
                setTemplateSelectionNeedsChange(false);
              }}
            >
              {selectedTemplateUnavailable && selectedTemplate && (
                <MenuItem disabled value={selectedTemplate.id}>
                  {selectedTemplate.displayName} — version {selectedTemplate.version} — unavailable
                </MenuItem>
              )}
              {templates.filter((template) => !selectedTemplateUnavailable || template.id !== selectedTemplateId).map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    <span>{template.displayName} — version {template.version}</span>
                    {template.isDefault && <Chip label="Default" size="small" />}
                    {template.negotiable && <Chip label="Negotiable" color="primary" size="small" />}
                    {template.participationMode === "StaffReview" && <Chip label="Staff review" size="small" />}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {selectedTemplateUnavailable && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                This onboarding template is no longer available or authorized. Select a different option before sending.
              </Typography>
            )}
          </FormControl>
        )}
      </OfficerInvitationFields>
      {pendingOperation && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          action={(
            <Button color="inherit" size="small" onClick={startNewInvitation}>
              Start new invitation
            </Button>
          )}
        >
          This invitation has an unknown delivery result. Check its status before starting another.
          {` Reference: ${pendingOperation.traceId ?? pendingOperation.id.value}.`}
        </Alert>
      )}
      <SendInvitationButton
        id="officer-invitation-submit-button"
        color="primary"
        disabled={
          sending ||
          (!pendingOperation &&
            (!formIsValid ||
              messageDebouncing ||
              selectedTemplateUnavailable ||
              (templateCatalogRequired && (templatesLoading || templatesError || !selectedTemplateId))))
        }
        loading={sending}
        onClick={sendInvitation}
        startIcon={<SendIcon />}
        variant="contained"
      >
        {pendingOperation ? "Check invitation status" : "Send Invite"}
      </SendInvitationButton>
    </OfficerInvitationFormContainer>
  );
}

function validateFormFields(
  formFields: Partial<Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo">>,
): asserts formFields is Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo"> {
  const missingFields: string[] = [];

  if (!formFields.title) missingFields.push("Title");
  else if (!(formFields.title instanceof Title)) throw new Error("Title is invalid type");

  if (!formFields.name) missingFields.push("Name");
  else if (!(formFields.name instanceof Name)) throw new Error("Name is invalid type");

  if (!formFields.email) missingFields.push("Email Address");
  else if (!(formFields.email instanceof EmailAddress)) throw new Error("Email Address is invalid type");

  if (!formFields.entityName) missingFields.push("Entity Name");
  else if (!(formFields.entityName instanceof EntityName)) throw new Error("Entity Name is invalid type");

  if (!formFields.entityLocation) missingFields.push("Entity Location");
  else if (!(formFields.entityLocation instanceof Address)) throw new Error("Entity Location is invalid type");

  if (missingFields.length > 0) {
    throw new InvalidEntityOfficerInvitationError(`Missing required fields: ${missingFields.join(", ")}`);
  }
}

function readPendingOfficerSetup(): { id: Guid; traceId?: string } | undefined {
  try {
    const stored = globalThis.localStorage?.getItem(PENDING_OFFICER_SETUP_STORAGE_KEY);
    if (!stored) return;
    const value = JSON.parse(stored) as { operationId?: string; traceId?: string };
    if (!value.operationId) return;
    return { id: new Guid(value.operationId), traceId: value.traceId };
  } catch {
    return;
  }
}

function writePendingOfficerSetup(pending: { id: Guid; traceId?: string }) {
  try {
    globalThis.localStorage?.setItem(PENDING_OFFICER_SETUP_STORAGE_KEY, JSON.stringify({
      operationId: pending.id.value,
      traceId: pending.traceId,
    }));
  } catch {
    // Status-only recovery remains available for this mounted form.
  }
}

function clearPendingOfficerSetup() {
  try {
    globalThis.localStorage?.removeItem(PENDING_OFFICER_SETUP_STORAGE_KEY);
  } catch {
    // Storage availability must not block a confirmed invitation from completing.
  }
}
